import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { Reveal, Label } from "@/components/Reveal";
import Parallax from "@/components/Parallax";
import TopoBackground from "@/components/TopoBackground";
import { MEDIA } from "@/data/content";

const q = (u) => `${u}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200`;

const HERO_IMG = "https://customer-assets.emergentagent.com/job_steward-memory/artifacts/pe66bqto_PV%20screening%20-%20community.webp";

const DOC_CARDS = [
  { name: "Seed Collection", img: q("https://images.unsplash.com/photo-1551855350-c86caeaf8707"), ex: "Linked to species, season, seed protocol and the restoration projects it supplies." },
  { name: "Traditional Ecological Knowledge", img: q("https://images.unsplash.com/photo-1755692880777-9ac765845a37"), ex: "Connected to place, seasons and the species it describes." },
  { name: "Medicinal Plants", img: q("https://images.unsplash.com/photo-1551772413-6c1b7dc18548"), ex: "Governed knowledge, shared only on community terms." },
  { name: "Language", img: q(MEDIA.community.split("?")[0]), ex: "Language names mapped to species, places and story." },
  { name: "Welcome to Country", img: q(MEDIA.placeWide.split("?")[0]), ex: "Held with cultural authority and protocol." },
  { name: "Women's Knowledge", img: q("https://images.unsplash.com/photo-1532092367580-3bd5bc78dd9d"), ex: "Access permissions remain with the women who hold it." },
  { name: "Men's Knowledge", img: q("https://images.unsplash.com/photo-1723367673687-2a3024f9cd7a"), ex: "Access permissions remain with the men who hold it." },
  { name: "Seasonal Calendars", img: q(MEDIA.mist.split("?")[0]), ex: "Tied to weather, flowering, fire and harvest cycles." },
  { name: "Fire Knowledge", img: q("https://images.unsplash.com/photo-1600181957881-b96c42bc6fab"), ex: "Cool-burn practice connected to Country and season." },
  { name: "Cultural Mapping", img: q("https://images.unsplash.com/photo-1657055725283-7f44c9201e76"), ex: "Knowledge anchored to Country through place." },
  { name: "Bush Foods", img: q("https://images.unsplash.com/photo-1768231408401-22a6b94ef318"), ex: "Species, season and preparation, held together." },
  { name: "Water Places", img: q(MEDIA.forestPath.split("?")[0]), ex: "Significance, access and stories of each water place." },
  { name: "Restoration Projects", img: q("https://images.unsplash.com/photo-1598335624134-5bceb5de202d"), ex: "Seed, knowledge holders and outcomes, linked over time." },
  { name: "Ranger Programs", img: q("https://images.unsplash.com/photo-1723367673687-2a3024f9cd7a"), ex: "Field practice recorded and passed on." },
  { name: "Sacred Sites (where appropriate)", img: q(MEDIA.mist.split("?")[0]), ex: "Recorded only with permission; access remains cultural." },
  { name: "Community Histories", img: q("https://images.unsplash.com/photo-1606092195808-3107b7ed4c98"), ex: "Memory held by community, for community." },
  { name: "Youth Interviews", img: q(MEDIA.closing.split("?")[0]), ex: "Young people become authors of continuity." },
  { name: "Oral Histories", img: q("https://images.unsplash.com/photo-1755692880777-9ac765845a37"), ex: "Voices preserved beside transcript and place." },
  { name: "Traditional Craft", img: q("https://images.unsplash.com/photo-1707978932202-751b08324daf"), ex: "Technique, material and meaning, recorded together." },
  { name: "Governance Stories", img: q("https://images.unsplash.com/photo-1550096141-7263640aa48c"), ex: "The decisions and law that guide stewardship." },
];

const ARCHIVE_FLOW = ["Country", "Gather", "Listen", "Record", "Review Together", "Archive", "Map to Place", "Future Generations"];

const LINKS_TO = ["Species", "Location", "Season", "Language", "Video", "Audio", "Transcript", "Seed protocol", "Restoration project", "Traditional Owner", "Youth interviews", "Historical recordings", "Related stories", "Future observations"];

const GOVERNANCE = ["Who records", "What is recorded", "Who may access it", "What remains private", "What becomes public", "How knowledge is used", "How future generations inherit it"];

const WORKFLOW = ["Community Gathering", "Participatory Video Workshop", "Story Circles", "Recording", "Community Review", "Archive Creation", "Knowledge Mapping", "Living Digital Archive", "Education", "Future Stewardship"];

const COMMUNITIES = ["Ranger Groups", "Traditional Owner Corporations", "Schools", "Cultural Centres", "Museums", "Indigenous Protected Areas", "Natural Resource Management", "Seed Enterprises", "Language Centres", "Researchers", "Local Government", "Youth Programs"];

export default function ParticipatoryVideo() {
  const [openCard, setOpenCard] = useState(null);

  return (
    <div data-testid="participatory-video-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <Parallax strength={70}>
          <img
            src={HERO_IMG}
            alt="A community gathered on Country at sunset for an outdoor screening"
            className="h-full w-full object-cover wk-kenburns"
          />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806]/70 via-[#050806]/45 to-[#050806]" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <Label className="mb-6 block">Communities documenting living knowledge</Label>
            <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.03] text-[#F4F1ED]">
              Participatory Video
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#E4E0D9] leading-relaxed font-light">
              Every community already holds extraordinary knowledge. The challenge is not
              creating it. The challenge is ensuring it is never lost.
            </p>
            <p className="mt-5 max-w-2xl text-base text-[#A8A5A1] leading-relaxed">
              Participatory Video enables communities to document stewardship, stories,
              language, ecological knowledge, restoration practices and cultural memory — in
              their own voices, on their own Country, under their own governance.
            </p>
            <Link
              to="#process"
              onClick={(e) => { e.preventDefault(); document.getElementById("process")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="pv-explore-button"
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500"
            >
              Explore the Process <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MORE THAN FILMMAKING */}
      <section id="process" className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/8">
              <img src="https://customer-assets.emergentagent.com/job_steward-memory/artifacts/tdnhwzza_participatory%20video.webp" alt="Community members documenting seed collection on Country at sunrise" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050806]/60 to-transparent" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Label className="block mb-4">More than filmmaking</Label>
              <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED]">
                The process is more transformative than the product.
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-8 space-y-5 text-lg text-[#A8A5A1] leading-relaxed">
                <p>
                  Participatory Video is often described as a method of making community
                  films. We see something much deeper — a process through which communities
                  reflect together, ask better questions, document living knowledge,
                  strengthen relationships and build an enduring archive for future generations.
                </p>
                <p>
                  The finished film is only one outcome. The real value lies in the
                  conversations, decisions and memories created throughout the process.
                </p>
              </div>
            </Reveal>
            <div className="mt-8 space-y-3">
              {["Every interview becomes cultural continuity.", "Every recording becomes stewardship intelligence.", "Every story strengthens the archive."].map((t, i) => (
                <Reveal key={t} delay={0.2 + i * 0.1}>
                  <p className="font-display text-xl md:text-2xl text-[#F4F1ED] border-l border-[#D4A373]/40 pl-5">{t}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FULL WIDTH QUOTE */}
      <section className="py-28 md:py-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.3] text-[#F4F1ED]">
              “Communities are not the subjects of documentation.{" "}
              <span className="text-[#D4A373]">They are the authors of their own knowledge.”</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* LIVING KNOWLEDGE ARCHIVE — flow */}
      <section className="relative py-24 md:py-32 border-y border-white/5 overflow-hidden">
        <TopoBackground opacity={0.05} />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">Building a living knowledge archive</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] max-w-3xl">
              From a moment on Country to knowledge that endures.
            </h2>
          </Reveal>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-3 gap-y-4">
            {ARCHIVE_FLOW.map((node, i) => (
              <Reveal key={node} delay={i * 0.06}>
                <span className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-[#0C110E] hover:border-[#D4A373]/50 transition-colors duration-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#D4A373]" />
                    <span className="font-display text-lg md:text-xl text-[#F4F1ED]">{node}</span>
                  </span>
                  {i < ARCHIVE_FLOW.length - 1 && <span className="text-[#D4A373]/50">→</span>}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-14 max-w-2xl mx-auto text-center text-[#A8A5A1] leading-relaxed">
              Each session becomes structured knowledge connected to places, species,
              language groups, seasons, restoration projects and community governance.
              Instead of isolated videos, WisdomKeepers creates a searchable, living archive
              of stewardship.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHAT CAN COMMUNITIES DOCUMENT — card grid */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">What can communities document?</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] max-w-3xl">
              Living knowledge, held in place and voice.
            </h2>
            <p className="mt-4 text-[#A8A5A1]">Select a card to see how each recording becomes searchable knowledge.</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {DOC_CARDS.map((c, i) => {
              const open = openCard === i;
              return (
                <button
                  key={c.name}
                  data-testid={`doc-card-${i}`}
                  onClick={() => setOpenCard(open ? null : i)}
                  onMouseEnter={() => setOpenCard(i)}
                  onMouseLeave={() => setOpenCard((v) => (v === i ? null : v))}
                  className="group relative aspect-[4/5] rounded-lg overflow-hidden text-left"
                >
                  <img src={c.img} alt={c.name} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/30 to-transparent" />
                  <div className={`absolute inset-0 bg-[#050806]/85 backdrop-blur-sm flex items-center p-5 transition-opacity duration-500 ${open ? "opacity-100" : "opacity-0"}`}>
                    <p className="text-sm text-[#E4E0D9] leading-relaxed">{c.ex}</p>
                  </div>
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-display text-lg md:text-xl text-[#F4F1ED] leading-tight">{c.name}</h3>
                      <Plus size={16} className={`text-[#D4A373] shrink-0 transition-transform duration-500 ${open ? "rotate-45" : ""}`} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* STEWARDSHIP INTELLIGENCE IN PRACTICE */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">Stewardship Intelligence in practice</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] max-w-3xl">
              Knowledge becomes relational. Not linear.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-col items-center">
              <div className="px-7 py-5 rounded-2xl border border-[#D4A373]/50 bg-[#101612] text-center max-w-md">
                <p className="wk-label mb-2">One recording</p>
                <p className="font-display text-2xl text-[#F4F1ED]">An Elder explains collecting Black Bean seed.</p>
              </div>
              <span className="my-6 h-10 w-px bg-gradient-to-b from-[#D4A373]/50 to-transparent" />
            </div>
          </Reveal>
          <div className="flex flex-wrap justify-center gap-3">
            {LINKS_TO.map((t, i) => (
              <Reveal key={t} delay={i * 0.04}>
                <span className="inline-block px-5 py-2.5 rounded-full border border-white/10 text-[#D8D4CE] hover:border-[#D4A373]/60 hover:text-[#D4A373] transition-colors duration-500">
                  {t}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-12 text-center text-[#A8A5A1] max-w-2xl mx-auto leading-relaxed">
              WisdomKeepers transforms isolated recordings into connected stewardship
              intelligence — every piece of knowledge holding its relationships to people,
              place and time.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMMUNITY-LED GOVERNANCE */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Label className="block mb-6">Community-led governance</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED]">
              Powerful only when communities remain in control.
            </h2>
            <p className="mt-6 text-[#A8A5A1] max-w-2xl mx-auto leading-relaxed">
              Built upon Indigenous data sovereignty and community governance. Communities
              determine:
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-3 text-left">
            {GOVERNANCE.map((g, i) => (
              <Reveal key={g} delay={i * 0.06}>
                <div className="px-6 py-4 rounded-xl border border-white/8 flex items-center gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4A373]" />
                  <span className="font-display text-xl text-[#F4F1ED]">{g}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-12 font-display text-2xl md:text-3xl text-[#D4A373]">
              Technology serves culture. Never the other way around.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PV AS RESTORATION */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden border-b border-white/5">
        <Parallax strength={60}>
          <img src={q("https://images.unsplash.com/photo-1598335624134-5bceb5de202d")} alt="People collecting seed together" className="h-full w-full object-cover" />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050806] via-[#050806]/70 to-[#050806]/20" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-28 w-full">
          <div className="max-w-xl">
            <Reveal>
              <Label className="block mb-4">Participatory Video as restoration</Label>
              <div className="space-y-2 font-display font-light text-2xl md:text-3xl text-[#F4F1ED] leading-snug">
                <p>Every restoration project begins with seed.</p>
                <p>Every seed carries knowledge.</p>
                <p>Every knowledge holder carries stories.</p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-lg text-[#D8D4CE] leading-relaxed">
                Participatory Video allows restoration to preserve not only landscapes, but
                the wisdom that makes restoration possible — stronger communities, stronger
                restoration and stronger cultural continuity.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WORKFLOW TIMELINE */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">The WisdomKeepers workflow</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] max-w-3xl">
              An ecosystem — not a production pipeline.
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {WORKFLOW.map((step, i) => (
              <Reveal key={step} delay={i * 0.05}>
                <div className="h-full p-6 rounded-xl border border-white/8 hover:border-[#D4A373]/40 hover:bg-[#101612] transition-all duration-500">
                  <span className="font-display text-3xl text-[#D4A373]/40">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-3 font-display text-xl text-[#F4F1ED] leading-tight">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DESIGNED FOR COMMUNITIES */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">Designed for communities</Label>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-3">
            {COMMUNITIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <span className="inline-block px-6 py-3 rounded-full border border-white/10 text-[#D8D4CE] hover:border-[#D4A373]/60 hover:text-[#D4A373] transition-colors duration-500">
                  {c}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden border-b border-white/5">
        <Parallax strength={60}>
          <img src={MEDIA.closing} alt="An Elder sharing knowledge with a young person" className="h-full w-full object-cover" />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/65 to-[#050806]/35" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-28 text-center">
          <Reveal>
            <p className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] leading-[1.25]">
              When an Elder shares knowledge with a young person, something precious happens.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl mx-auto text-lg text-[#D8D4CE] leading-relaxed">
              When that moment is recorded respectfully, governed by community, connected to
              Country and preserved for future generations, it becomes part of humanity's
              oldest living intelligence. Participatory Video is not simply about recording
              stories — it is about ensuring stewardship itself can continue.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 md:py-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-[#F4F1ED]">
              Build your community archive.
            </h2>
            <p className="mt-6 text-lg text-[#A8A5A1] max-w-2xl mx-auto leading-relaxed">
              Whether documenting native seed, ecological knowledge, language, restoration
              projects or oral histories, WisdomKeepers provides the framework for communities
              to create enduring, searchable archives of living knowledge.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-11 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/partner" data-testid="pv-start-project" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500">
                Start a Community Project
              </Link>
              <Link to="/stewardship-intelligence" data-testid="pv-explore-wk" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md border border-white/25 text-[#F4F1ED] hover:border-[#D4A373] hover:text-[#D4A373] transition-colors duration-500">
                Explore WisdomKeepers
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
